import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { serviceApi, businessApi } from '../services/api';
import { Service, Business } from '../types';
import { Modal } from '../components/Modal';

const serviceSchema = z.object({
  businessId: z.string().min(1, 'Business is required'),
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  duration: z.number().min(5, 'Duration must be at least 5 minutes'),
  price: z.number().min(0, 'Price must be positive'),
  maxParticipants: z.number().min(1, 'Must allow at least 1 participant'),
});

type ServiceForm = z.infer<typeof serviceSchema>;

export function Services() {
  const [searchParams] = useSearchParams();
  const initialBusinessId = searchParams.get('businessId') || '';
  
  const [services, setServices] = useState<Service[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ServiceForm>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      businessId: initialBusinessId,
      duration: 60,
      price: 0,
      maxParticipants: 1,
    },
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const businessesRes = await businessApi.getMyBusinesses();
      const businesses = businessesRes.data.data.businesses || [];
      setBusinesses(businesses);

      if (businesses.length > 0) {
        const servicesRes = await serviceApi.getBusinessServices(businesses[0].id);
        setServices(servicesRes.data.data.services || []);
      } else {
        setServices([]);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: ServiceForm) => {
    try {
      if (editingService) {
        await serviceApi.updateService(editingService.id, data as any);
        toast.success('Service updated successfully');
      } else {
        await serviceApi.createService(data as any);
        toast.success('Service created successfully');
      }
      setIsModalOpen(false);
      setEditingService(null);
      reset();
      fetchData();
    } catch (error) {
      toast.error(editingService ? 'Failed to update service' : 'Failed to create service');
    }
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    reset({
      businessId: service.businessId,
      name: service.name,
      description: service.description || '',
      duration: service.duration,
      price: service.price,
      maxParticipants: service.maxParticipants,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this service?')) return;
    
    try {
      await serviceApi.deleteService(id);
      toast.success('Service deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const handleAddNew = () => {
    setEditingService(null);
    reset({
      businessId: businesses[0]?.id || '',
      name: '',
      description: '',
      duration: 60,
      price: 0,
      maxParticipants: 1,
    });
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-secondary-900">Services</h1>
        <button onClick={handleAddNew} className="btn-primary">
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Service
        </button>
      </div>

      {services.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase">
                  Max Participants
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {services.map((service) => (
                <tr key={service.id}>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-secondary-900">{service.name}</p>
                      <p className="text-sm text-secondary-500">{service.description}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-secondary-500">{service.duration} min</td>
                  <td className="px-6 py-4 text-sm text-secondary-500">${service.price}</td>
                  <td className="px-6 py-4 text-sm text-secondary-500">{service.maxParticipants}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        service.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {service.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button
                      onClick={() => handleEdit(service)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(service.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <p className="text-secondary-500 mb-4">No services yet.</p>
          <button onClick={handleAddNew} className="btn-primary">
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Your First Service
          </button>
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingService(null);
        }}
        title={editingService ? 'Edit Service' : 'Add New Service'}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {!editingService && (
            <div>
              <label className="label">Business *</label>
              <select {...register('businessId')} className="input">
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              {errors.businessId && (
                <p className="text-sm text-red-600 mt-1">{errors.businessId.message}</p>
              )}
            </div>
          )}

          <div>
            <label className="label">Service Name *</label>
            <input {...register('name')} type="text" className="input" />
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>}
          </div>

          <div>
            <label className="label">Description</label>
            <textarea {...register('description')} className="input" rows={2} />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">Duration (min) *</label>
              <input
                {...register('duration', { valueAsNumber: true })}
                type="number"
                min={5}
                className="input"
              />
              {errors.duration && (
                <p className="text-sm text-red-600 mt-1">{errors.duration.message}</p>
              )}
            </div>
            <div>
              <label className="label">Price ($) *</label>
              <input
                {...register('price', { valueAsNumber: true })}
                type="number"
                min={0}
                step="0.01"
                className="input"
              />
              {errors.price && (
                <p className="text-sm text-red-600 mt-1">{errors.price.message}</p>
              )}
            </div>
            <div>
              <label className="label">Max Participants *</label>
              <input
                {...register('maxParticipants', { valueAsNumber: true })}
                type="number"
                min={1}
                className="input"
              />
              {errors.maxParticipants && (
                <p className="text-sm text-red-600 mt-1">{errors.maxParticipants.message}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                setEditingService(null);
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? 'Saving...' : editingService ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
